import { POSITIONS } from './draftBuildSpectrum';

/** Radar spoke layout: top → clockwise (QB top, WR right, TE bottom, RB left). */
export const RADAR_SPOKE_ORDER = ['QB', 'WR', 'TE', 'RB'];

/** Apex-friendly hex aligned with POSITION_COLORS on the spectrum page. */
export const SPECTRUM_CHART_COLORS = {
	QB: '#3b82f6',
	RB: '#22c55e',
	WR: '#eab308',
	TE: '#a855f7'
};

function hexToRgba(hex, alpha) {
	const normalized = hex.replace('#', '');
	const r = parseInt(normalized.slice(0, 2), 16);
	const g = parseInt(normalized.slice(2, 4), 16);
	const b = parseInt(normalized.slice(4, 6), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function blendHexColors(hexA, hexB, ratio = 0.5, alpha = 1) {
	const parse = (hex) => {
		const n = hex.replace('#', '');
		return [
			parseInt(n.slice(0, 2), 16),
			parseInt(n.slice(2, 4), 16),
			parseInt(n.slice(4, 6), 16)
		];
	};
	const [r1, g1, b1] = parse(hexA);
	const [r2, g2, b2] = parse(hexB);
	const mix = (a, b) => Math.round(a * (1 - ratio) + b * ratio);
	return `rgba(${mix(r1, r2)}, ${mix(g1, g2)}, ${mix(b1, b2)}, ${alpha})`;
}

function radarWebColors(isDarkMode) {
	const connectorAlpha = isDarkMode ? 0.38 : 0.3;
	const outerFill = isDarkMode ? 'rgba(55, 65, 81, 0.2)' : 'rgba(243, 244, 246, 0.85)';
	const outerStroke = isDarkMode ? '#4b5563' : '#d1d5db';
	return {
		fill: [outerFill],
		stroke: [outerStroke],
		connector: RADAR_SPOKE_ORDER.map((pos, index) => {
			const next = RADAR_SPOKE_ORDER[(index + 1) % RADAR_SPOKE_ORDER.length];
			return blendHexColors(
				SPECTRUM_CHART_COLORS[pos],
				SPECTRUM_CHART_COLORS[next],
				0.5,
				connectorAlpha
			);
		})
	};
}

function radarDiscreteMarkers(seriesIndex, isDarkMode) {
	const stroke = isDarkMode ? '#111827' : '#ffffff';
	return RADAR_SPOKE_ORDER.map((pos, dataPointIndex) => ({
		seriesIndex,
		dataPointIndex,
		fillColor: SPECTRUM_CHART_COLORS[pos],
		strokeColor: stroke,
		strokeWidth: 2,
		size: seriesIndex === 0 ? 6 : 4
	}));
}

/** Radar spoke scale — outer ring = position cap for normalization. */
export const RADAR_POSITION_MAX = { QB: 5, RB: 7, WR: 10, TE: 4 };

function radarSpokeValue(pos, count) {
	const max = RADAR_POSITION_MAX[pos] || 1;
	return Math.min(100, Math.round((count / max) * 100));
}

export const SPECTRUM_VIEW_MODES = [
	{ id: 'map', label: 'Round map' },
	{ id: 'curve', label: 'Build curve' },
	{ id: 'heatmap', label: 'Focus map' },
	{ id: 'shape', label: 'Roster shape' }
];

function chartTextColor(isDarkMode) {
	return isDarkMode ? '#e5e7eb' : '#374151';
}

function chartMutedColor(isDarkMode) {
	return isDarkMode ? '#9ca3af' : '#6b7280';
}

function chartGridColor(isDarkMode) {
	return isDarkMode ? '#374151' : '#e5e7eb';
}

function roundCategories(plan) {
	return plan.rounds.map((r) => `R${r.round}`);
}

function buildRoundAnnotations(plan, isDarkMode) {
	const lines = [];
	const labelColor = chartMutedColor(isDarkMode);

	if (plan.heroPauseUntil) {
		lines.push({
			x: `R${plan.heroPauseUntil}`,
			borderColor: SPECTRUM_CHART_COLORS.RB,
			strokeDashArray: 4,
			label: {
				text: 'Hero revisit',
				style: { color: labelColor, fontSize: '10px', fontWeight: 600 }
			}
		});
	}

	(plan.eliteAnchors || []).forEach((anchor) => {
		lines.push({
			x: `R${anchor.round}`,
			borderColor: SPECTRUM_CHART_COLORS[anchor.pos] || '#f59e0b',
			strokeDashArray: 2,
			label: {
				text: `Elite ${anchor.pos}`,
				style: { color: labelColor, fontSize: '10px' }
			}
		});
	});

	(plan.lateQBRounds || []).forEach((r) => {
		lines.push({
			x: `R${r}`,
			borderColor: SPECTRUM_CHART_COLORS.QB,
			strokeDashArray: 5,
			label: {
				text: 'Late QB',
				style: { color: labelColor, fontSize: '10px' }
			}
		});
	});

	(plan.lateTERounds || []).forEach((r) => {
		lines.push({
			x: `R${r}`,
			borderColor: SPECTRUM_CHART_COLORS.TE,
			strokeDashArray: 5,
			label: {
				text: 'Late TE',
				style: { color: labelColor, fontSize: '10px' }
			}
		});
	});

	return lines;
}

function baseChartOptions(isDarkMode) {
	return {
		chart: {
			toolbar: { show: false },
			fontFamily: 'inherit',
			background: 'transparent',
			animations: { enabled: true, easing: 'easeinout', speed: 450 }
		},
		theme: { mode: 'light' },
		grid: {
			borderColor: chartGridColor(isDarkMode),
			strokeDashArray: 3
		},
		tooltip: { theme: 'light' }
	};
}

/**
 * Stacked area — cumulative roster geometry by round (the "capital allocation" curve).
 */
export function buildCumulativeAreaChart(plan, { isDarkMode = false } = {}) {
	const categories = roundCategories(plan);
	const series = POSITIONS.map((pos) => ({
		name: pos,
		data: plan.rounds.map((r) => r.cumulative[pos])
	}));

	const options = {
		...baseChartOptions(isDarkMode),
		grid: {
			...baseChartOptions(isDarkMode).grid,
			padding: { right: 22 }
		},
		chart: {
			...baseChartOptions(isDarkMode).chart,
			type: 'area',
			stacked: true,
			selection: { enabled: true }
		},
		colors: POSITIONS.map((pos) => SPECTRUM_CHART_COLORS[pos]),
		stroke: { curve: 'smooth', width: 1.5 },
		fill: {
			type: 'gradient',
			gradient: {
				opacityFrom: 0.72,
				opacityTo: 0.28,
				shadeIntensity: 0.4
			}
		},
		dataLabels: { enabled: false },
		xaxis: {
			categories,
			labels: { style: { colors: chartMutedColor(isDarkMode), fontSize: '11px' } },
			title: {
				text: 'Draft round',
				style: { color: chartTextColor(isDarkMode), fontSize: '12px', fontWeight: 600 }
			}
		},
		yaxis: {
			min: 0,
			max: plan.totalRounds,
			tickAmount: Math.min(plan.totalRounds, 8),
			labels: { style: { colors: chartMutedColor(isDarkMode) } },
			title: {
				text: 'Cumulative picks by position',
				style: { color: chartTextColor(isDarkMode), fontSize: '12px', fontWeight: 600 }
			}
		},
		legend: {
			position: 'top',
			horizontalAlign: 'left',
			labels: { colors: chartTextColor(isDarkMode) }
		},
		annotations: {
			xaxis: buildRoundAnnotations(plan, isDarkMode)
		},
		tooltip: {
			theme: 'light',
			y: { formatter: (val) => `${val} players` },
			x: { formatter: (val) => String(val) }
		}
	};

	return { series, options, type: 'area', height: 380 };
}

/**
 * Heatmap — round × position focus weights (% engine emphasis).
 */
export function buildFocusHeatmapChart(plan, { isDarkMode = false } = {}) {
	const categories = roundCategories(plan);
	const primaryByRound = Object.fromEntries(
		plan.rounds.map((r) => [`R${r.round}`, r.primary])
	);

	/** Apex heatmap: one series per row (position); cell value is numeric `y`. */
	const series = POSITIONS.map((pos) => ({
		name: pos,
		data: plan.rounds.map((r) => ({
			x: `R${r.round}`,
			y: Math.round(r.weights[pos] * 100)
		}))
	}));

	const options = {
		chart: {
			type: 'heatmap',
			toolbar: { show: false },
			fontFamily: 'inherit',
			background: 'transparent',
			animations: { enabled: true, easing: 'easeinout', speed: 450 },
			selection: { enabled: true }
		},
		theme: { mode: 'light' },
		plotOptions: {
			heatmap: {
				shadeIntensity: 0.55,
				radius: 2,
				useFillColorAsStroke: false,
				colorScale: {
					ranges: isDarkMode
						? [
								{ from: 0, to: 20, color: '#374151', name: 'Low' },
								{ from: 21, to: 45, color: '#4b5563', name: 'Lean' },
								{ from: 46, to: 65, color: '#2563eb', name: 'Focus' },
								{ from: 66, to: 100, color: '#1e3a8a', name: 'Lock' }
							]
						: [
								{ from: 0, to: 20, color: '#e5e7eb', name: 'Low' },
								{ from: 21, to: 45, color: '#93c5fd', name: 'Lean' },
								{ from: 46, to: 65, color: '#3b82f6', name: 'Focus' },
								{ from: 66, to: 100, color: '#1d4ed8', name: 'Lock' }
							]
				}
			}
		},
		dataLabels: {
			enabled: true,
			style: { fontSize: '11px', fontWeight: 600, colors: [isDarkMode ? '#f9fafb' : '#111827'] },
			formatter: (val) => (val >= 18 ? `${val}%` : '')
		},
		stroke: { width: 1, colors: [isDarkMode ? '#1f2937' : '#ffffff'] },
		grid: { padding: { right: 8, left: 4 } },
		xaxis: {
			type: 'category',
			categories,
			labels: {
				rotate: plan.totalRounds > 16 ? -45 : 0,
				rotateAlways: plan.totalRounds > 16,
				style: { colors: chartMutedColor(isDarkMode), fontSize: '10px' }
			},
			title: {
				text: 'Draft round',
				style: { color: chartTextColor(isDarkMode), fontSize: '12px', fontWeight: 600 }
			}
		},
		yaxis: {
			labels: { style: { colors: chartTextColor(isDarkMode), fontWeight: 600 } }
		},
		tooltip: {
			theme: 'light',
			custom: ({ seriesIndex, dataPointIndex, w }) => {
				const pos = w.config.series[seriesIndex]?.name || '';
				const point = w.config.series[seriesIndex]?.data[dataPointIndex];
				if (!point) return '';
				const primary = primaryByRound[point.x];
				const color = SPECTRUM_CHART_COLORS[primary] || '#3b82f6';
				return `<div style="padding:8px 10px;font-size:13px">
					<div style="font-weight:600;margin-bottom:4px">${point.x} · ${pos}</div>
					<div>Focus: <strong>${point.y}%</strong></div>
					<div style="margin-top:4px;color:${color}">Pick: <strong>${primary}</strong></div>
				</div>`;
			}
		},
		legend: { show: false }
	};

	return {
		series,
		options,
		type: 'heatmap',
		height: Math.max(260, 52 * POSITIONS.length + 80)
	};
}

/**
 * Radar — this build's projected composition on each position scale (outer edge = 100%).
 */
export function buildCompositionRadarChart(plan, { isDarkMode = false } = {}) {
	const buildCounts = RADAR_SPOKE_ORDER.map((pos) => plan.rosterProjection[pos]);
	const buildSeries = RADAR_SPOKE_ORDER.map((pos) => radarSpokeValue(pos, plan.rosterProjection[pos]));
	const web = radarWebColors(isDarkMode);

	const options = {
		...baseChartOptions(isDarkMode),
		chart: { ...baseChartOptions(isDarkMode).chart, type: 'radar' },
		colors: ['#2563eb'],
		stroke: { width: 2.5, colors: ['#2563eb'] },
		fill: { opacity: 0.18 },
		markers: {
			size: 0,
			hover: { size: 7 },
			discrete: radarDiscreteMarkers(0, isDarkMode)
		},
		xaxis: {
			categories: [...RADAR_SPOKE_ORDER],
			labels: {
				style: {
					colors: RADAR_SPOKE_ORDER.map((pos) => SPECTRUM_CHART_COLORS[pos]),
					fontSize: '13px',
					fontWeight: 700
				}
			}
		},
		yaxis: {
			show: true,
			min: 0,
			max: 100,
			tickAmount: 4,
			labels: {
				style: { colors: chartMutedColor(isDarkMode), fontSize: '10px' },
				formatter: (val) => `${Math.round(val)}%`
			}
		},
		legend: { show: false },
		tooltip: {
			theme: 'light',
			custom: ({ dataPointIndex, w }) => {
				const pos = RADAR_SPOKE_ORDER[dataPointIndex];
				const count = buildCounts[dataPointIndex];
				const pct = w.globals.series[0][dataPointIndex];
				const color = SPECTRUM_CHART_COLORS[pos];
				return `<div style="padding:8px 10px;font-size:13px">
					<div style="font-weight:600;margin-bottom:4px;color:${color}">This build · ${pos}</div>
					<div><strong>${count}</strong> · ${pct}% of position scale</div>
				</div>`;
			}
		},
		plotOptions: {
			radar: {
				size: 140,
				polygons: {
					strokeColors: web.stroke,
					strokeWidth: 1,
					connectorColors: web.connector,
					fill: { colors: web.fill }
				}
			}
		}
	};

	return {
		series: [{ name: 'This build', data: buildSeries }],
		options,
		type: 'radar',
		height: 360,
		buildCounts
	};
}
